/**
 * SettingsView — the settings home (SETTINGS-NAV-02, Seth 2026-09-23: "make
 * sure the settings overall are still best laid out, searchable, organized.
 * Everything wired and working. Easy to navigate and use.").
 *
 *   · One map: `settingsIndex.ts` (NAV + ITEMS + search). The left rail is a
 *     compact grouped list — People · Scheduling · Money · Customer Messages ·
 *     Lists & Records · System — and includes the settings that live on other
 *     pages (↗), so this is the front door to all of them.
 *   · Search finds sections AND the settings inside them ("trip charge",
 *     "quiet hours", "level names"), ranks title hits first, ↑ ↓ Enter, "/"
 *     to focus. Opening an inside setting scrolls to it and outlines it.
 *   · The URL is the state: ?section= (plus a tab/view when a setting lives
 *     on one), so refresh, Back and bookmarks keep your place; with no
 *     section in the URL you land where you last were.
 *   · Each section gets ONE header here (title, with the description behind
 *     an (i) — DESC-CLEAN-01); sections no longer repeat their own.
 * SETTINGS-AUDIT-01: the rail shows only what this login's capabilities
 * unlock; nothing at all → the "ask the owner" card.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight, CornerDownLeft, MailX, Search, Settings as SettingsIcon, Sparkles, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/ui/PageHeader";
import { InfoTip } from "@/components/InfoTip";
import { useCan, usePermissions } from "@/lib/permissions/PermissionProvider";
import {
  useOrgSettingNumber,
  useSaveOrgSetting,
} from "@/lib/orgSettings";
import { DEFAULT_ROUTINE_CAP } from "@/features/board/suggestTimes";
import { PeopleAccessView } from "./access/PeopleAccessView";
import { ServiceTypesSection, PricingSection } from "./ServiceCatalogAdmin";
import { PlanPricingSection } from "./PlanPricingAdmin";
import { RulesSection } from "./RulesSection";
import { EquipmentCategoriesAdmin } from "./EquipmentCategoriesAdmin";
import { BrandsAdmin } from "./BrandsAdmin";
import { JurisdictionsAdmin } from "./JurisdictionsAdmin";
import { JobDocsPermitsAdmin } from "./JobDocsPermitsAdmin";
import { InstallChecklistAdmin } from "./InstallChecklistAdmin";
import { InstallDetailsAdmin } from "./InstallDetailsAdmin";
import { SoldHandoffSettings } from "./SoldHandoffSettings";
import { NewConstructionSettings } from "./NewConstructionSettings";
import { PostInstallSection } from "./PostInstallSection";
import { TemplatesSection } from "./TemplatesSection";
import { InboxTagsSection } from "./InboxTagsAdmin";
import { LeadSourcesAdmin } from "./LeadSourcesAdmin";
import { TeamSection } from "./TeamSection";
import { AddressAdminSection } from "./AddressAdmin";
import { InvoiceSettingsSection } from "./InvoiceSettingsSection";
import { FieldPaySettings } from "./FieldPaySettings";
import { QuotesSection } from "./QuotesAdmin";
import { OncallPhoneSection } from "./OncallPhoneSection";
import { AuditLogSection } from "./AuditLogView";
import { InstallWeekSettings } from "./InstallWeekSettings";
import { SkillsLevelsSection } from "./SkillsLevelsSection";
import { TechRequestsSection } from "./TechRequestsAdmin";
import { RemindersSection } from "./RemindersSection";
import { AskPegasusAdmin } from "./AskPegasusAdmin";
import { LiveSwitchesSection } from "./LiveSwitchesSection";
import { ConnectionsSection } from "./ConnectionsSection";
import { useCommsLive } from "@/features/comms/comms";
import { GROUP_ORDER, NAV, searchSettings, type Hit, type NavEntry, type SectionDef, type SectionKey } from "./settingsIndex";

/** SETTINGS-TRUTH-01 (the canvas pass, Seth 2026-09-29): the one system state
 *  that changes what every comms setting MEANS — said plainly on the front
 *  door, not just on the Automations page. Renders nothing once live. */
function SendingOffStrip() {
  const navigate = useNavigate();
  const { data: live, isSuccess } = useCommsLive();
  if (!isSuccess || live) return null;
  return (
    <button
      type="button"
      onClick={() => navigate("/automations")}
      className="mb-5 flex w-full items-center gap-2.5 rounded-2xl border border-amber-300/70 bg-amber-50/70 px-4 py-2.5 text-left shadow-sm transition-colors hover:bg-amber-100/60"
    >
      <MailX className="h-4 w-4 shrink-0 text-amber-600" />
      <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-amber-900">
        Sending is OFF — every customer message holds in the Outbox until it&apos;s flipped live.
      </span>
      <span className="shrink-0 text-xs font-semibold text-amber-800">Open Automations →</span>
    </button>
  );
}

/** Old section names still land somewhere sensible. */
const ALIAS: Record<string, { section: SectionKey; params?: Record<string, string> }> = {
  users: { section: "access" },
  security: { section: "access", params: { tab: "signin" } },
};
const LAST_KEY = "pegasus.settings.lastSection";
const readLast = (): string | null => { try { return window.localStorage.getItem(LAST_KEY); } catch { return null; } };
const writeLast = (k: string) => { try { window.localStorage.setItem(LAST_KEY, k); } catch { /* private window */ } };
/** The outline a found setting gets for a moment. */
const FLASH = ["ring-2", "ring-brand-400/60", "ring-offset-2", "rounded-xl"];
/** A heading's own words, without the "i" of an InfoTip (i) placed inside it
 *  (DESC-CLEAN-01), so a found setting still matches its heading. */
const headingText = (n: HTMLElement): string => {
  const tips = [...n.querySelectorAll<HTMLElement>("button[aria-expanded]")].filter((b) => b.textContent === "i");
  if (!tips.length) return n.textContent ?? "";
  const copy = n.cloneNode(true) as HTMLElement;
  copy.querySelectorAll<HTMLElement>("button[aria-expanded]").forEach((b) => { if (b.textContent === "i") b.remove(); });
  return copy.textContent ?? "";
};

export function SettingsView() {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const entries = useMemo(() => NAV.filter((e) => can(e.cap)), [can]);
  const sections = entries.filter((e): e is SectionDef => e.kind === "section");

  // The URL is the state (?section=…), so refresh / Back / bookmarks keep your place.
  const [params, setParams] = useSearchParams();
  const raw = params.get("section");
  const alias = raw ? ALIAS[raw] : undefined;
  useEffect(() => {
    if (!alias) return;
    const p = new URLSearchParams();
    p.set("section", alias.section);
    for (const [k, v] of Object.entries(alias.params ?? {})) p.set(k, v);
    setParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);
  const want = alias?.section ?? raw ?? readLast();
  const section: SectionKey | null = sections.some((x) => x.key === want) ? (want as SectionKey) : sections[0]?.key ?? null;
  const current = sections.find((x) => x.key === section) ?? null;
  useEffect(() => { if (section) writeLast(section); }, [section]);

  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const hits = useMemo(() => searchSettings(q, entries), [q, entries]);
  useEffect(() => setActive(0), [q]);
  const searchRef = useRef<HTMLInputElement>(null);
  // "/" jumps to the search box from anywhere on the page (not while typing).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // A found setting: open its section (and tab), then scroll to its heading.
  const contentRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<string | null>(null);
  useEffect(() => {
    if (!anchor) return;
    const wantText = anchor.toLowerCase();
    let tries = 0;
    let timer = 0;
    const tick = () => {
      const root = contentRef.current;
      const el = root
        ? [...root.querySelectorAll<HTMLElement>("h2,h3,h4,h5,legend,label,button,span,div,th,summary,p")]
            .find((n) => n.childElementCount <= 3 && headingText(n).trim().toLowerCase() === wantText) ?? null
        : null;
      if (el) {
        const center = () => el.scrollIntoView({ block: "center", behavior: "smooth" });
        center();
        const box = (el.closest("section, fieldset, .rounded-xl, .rounded-2xl") as HTMLElement | null) ?? el;
        box.classList.add(...FLASH);
        window.setTimeout(() => box.classList.remove(...FLASH), 1800);
        // A section loads its lists after it opens, which can push the card
        // down — keep it in view for a moment, unless the person scrolls.
        let moved = false;
        const stop = () => { moved = true; };
        const evs = ["wheel", "touchstart", "keydown", "mousedown"] as const;
        evs.forEach((ev) => window.addEventListener(ev, stop, { passive: true }));
        [400, 900, 1600].forEach((ms) => window.setTimeout(() => {
          if (moved || !el.isConnected) return;
          const r = el.getBoundingClientRect();
          if (Math.abs(r.top + r.height / 2 - window.innerHeight / 2) > 140) center();
        }, ms));
        window.setTimeout(() => evs.forEach((ev) => window.removeEventListener(ev, stop)), 1700);
        setAnchor(null);
      } else if (tries++ < 20) {
        timer = window.setTimeout(tick, 150);
      } else {
        setAnchor(null);
      }
    };
    timer = window.setTimeout(tick, 60);
    return () => window.clearTimeout(timer);
  }, [anchor, section]);

  const openSection = (k: SectionKey, extra?: Record<string, string>) => {
    const p = new URLSearchParams();
    p.set("section", k);
    for (const [key, v] of Object.entries(extra ?? {})) p.set(key, v);
    setParams(p);
    setQ("");
    contentRef.current?.closest("main")?.scrollTo({ top: 0 });
  };
  const openEntry = (e: NavEntry) => (e.kind === "link" ? navigate(e.to) : openSection(e.key));
  const openHit = (h: Hit) => {
    if (h.entry.kind === "link") { navigate(h.entry.to); return; }
    openSection(h.entry.key, h.item?.params);
    if (h.item?.anchor) setAnchor(h.item.anchor);
  };

  if (!current) {
    return (
      <div className="mx-auto max-w-xl p-8">
        <div className="rounded-2xl border border-hairline bg-white p-8 text-center shadow-e1">
          <SettingsIcon className="mx-auto h-8 w-8 text-slate-300" />
          <h2 className="mt-3 text-lg font-bold text-slate-800">Settings is locked for your account</h2>
          <p className="mt-1.5 text-sm text-slate-500">
            Company settings can change pricing, permissions, and what customers receive — so they need
            a settings capability. Ask the owner to grant access under People &amp; Access if you need in here.
          </p>
        </div>
      </div>
    );
  }

  const row = "group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left transition-colors";

  return (
    <div className="mx-auto max-w-7xl p-8">
      <PageHeader icon={SettingsIcon} title="Settings" description="Everything Pegasus does the way Sedgwick Heating does it — search for a setting, or browse by group." className="mb-6" />

      {/* SETTINGS-TRUTH-01: while comms are held, say so where settings live. */}
      <SendingOffStrip />

      <div className="grid items-start gap-6 lg:grid-cols-[264px_minmax(0,1fr)]">
        <nav aria-label="Settings" className="space-y-3 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6.5rem)] lg:overflow-y-auto lg:pb-4">
          {/* THE FINDER — sections and the settings inside them; ↑ ↓ Enter, "/" from anywhere. */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, Math.max(hits.length - 1, 0))); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
                else if (e.key === "Enter" && hits[active]) { e.preventDefault(); openHit(hits[active]); }
                else if (e.key === "Escape") setQ("");
              }}
              placeholder="Search settings"
              aria-label="Search settings"
              className="w-full rounded-full border border-hairline bg-white py-2 pl-9 pr-9 text-[13.5px] shadow-e1 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
            {q ? (
              <button type="button" onClick={() => setQ("")} aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <X className="h-3.5 w-3.5" />
              </button>
            ) : (
              <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-hairline px-1.5 text-[10.5px] font-semibold text-slate-400">/</kbd>
            )}
          </div>

          {q.trim() ? (
            <div role="listbox" aria-label="Matching settings" className="space-y-0.5">
              {hits.length === 0 ? (
                <p className="px-2.5 py-2 text-[12.5px] text-slate-500">
                  Nothing matches. Try a plainer word — “hours”, “price”, “text”, “roster”.
                </p>
              ) : (
                hits.map((h, i) => {
                  const Icon = h.entry.icon;
                  return (
                    <button key={`${h.entry.key}:${h.item?.title ?? ""}`} type="button" role="option" aria-selected={i === active}
                      onMouseEnter={() => setActive(i)} onClick={() => openHit(h)}
                      className={cn(row, "items-start py-2", i === active ? "bg-white shadow-sm ring-1 ring-brand-200" : "hover:bg-white/70")}>
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold text-slate-800">{h.item ? h.item.title : h.entry.title}</span>
                        <span className="block truncate text-[11.5px] text-slate-500">
                          {h.item ? `in ${h.entry.title}` : h.entry.group}
                          {h.entry.kind === "link" ? ` · on the ${h.entry.where} page` : ""}
                        </span>
                      </span>
                      {h.entry.kind === "link" ? <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /> : i === active ? <CornerDownLeft className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300" /> : null}
                    </button>
                  );
                })
              )}
            </div>
          ) : (
            GROUP_ORDER.map((g) => {
              const inGroup = entries.filter((e) => e.group === g);
              if (!inGroup.length) return null;
              return (
                <div key={g}>
                  <div className="mb-0.5 px-2.5 pt-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">{g}</div>
                  {inGroup.map((e) => {
                    const on = e.kind === "section" && e.key === section;
                    const Icon = e.icon;
                    return (
                      <button key={e.key} type="button" onClick={() => openEntry(e)} aria-current={on ? "page" : undefined}
                        title={e.kind === "link" ? `${e.desc} (opens the ${e.where} page)` : e.desc}
                        className={cn(row, on ? "bg-white text-brand-800 shadow-sm ring-1 ring-brand-200" : "text-slate-700 hover:bg-white/70")}>
                        <Icon className={cn("h-4 w-4 shrink-0", on ? "text-brand-600" : "text-slate-400")} />
                        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{e.title}</span>
                        {e.kind === "link" && <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-slate-300 group-hover:text-slate-500" aria-label="Opens another page" />}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </nav>

        <div ref={contentRef} className="min-w-0">
          {/* ONE header per section — the sections no longer repeat their own. */}
          <div className="mb-4 flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--og-soft)] text-slate-600">
              <current.icon className="h-5 w-5" />
            </span>
            <h3 className="flex min-w-0 items-center gap-2 text-[22px] font-extrabold leading-tight tracking-tight text-slate-900">
              {current.title}
              <InfoTip label="About this section">{current.desc}</InfoTip>
            </h3>
          </div>
          {section === "access" && <PeopleAccessView />}
          {section === "team" && <TeamSection />}
          {section === "skills" && <SkillsLevelsSection />}
          {section === "tech_requests" && <TechRequestsSection />}
          {section === "service_types" && <ServiceTypesSection />}
          {section === "rules" && <RulesSection />}
          {section === "oncall" && <OncallPhoneSection />}
          {section === "install_week" && <InstallWeekSettings />}
          {section === "ai" && <AiControlsSection />}
          {section === "pricing" && <PricingSection />}
          {section === "plan_pricing" && <PlanPricingSection />}
          {section === "quotes" && <QuotesSection />}
          {section === "invoices" && <InvoiceSettingsSection />}
          {section === "field_pay" && <FieldPaySettings />}
          {section === "templates" && <TemplatesSection />}
          {section === "reminders" && <RemindersSection />}
          {section === "post_install" && <PostInstallSection />}
          {section === "inbox_tags" && <InboxTagsSection />}
          {section === "equipment_categories" && <EquipmentCategoriesAdmin />}
          {section === "brands" && <BrandsAdmin />}
          {section === "lead_sources" && <LeadSourcesAdmin />}
          {section === "jurisdictions" && <JurisdictionsAdmin />}
          {section === "job_docs" && <JobDocsPermitsAdmin />}
          {section === "install_checklist" && <InstallChecklistAdmin />}
          {section === "install_details" && <InstallDetailsAdmin />}
          {section === "sold_handoff" && <SoldHandoffSettings />}
          {section === "new_construction" && <NewConstructionSettings />}
          {section === "addresses" && <AddressAdminSection />}
          {section === "live" && <LiveSwitchesSection />}
          {section === "connections" && <ConnectionsSection />}
          {section === "ask" && <AskPegasusAdmin />}
          {section === "audit" && <AuditLogSection />}
        </div>
      </div>
    </div>
  );
}


/**
 * AI controls (SCHED-AI-02) — the self-service knobs for Pegasus's derived
 * suggestions. First knob: the routine headroom cap. More land here as the
 * copilot grows (urgency keyword review, capacity by season, metrics review).
 */
function AiControlsSection() {
  const canManage = useCan("settings.scheduling");
  const cap = useOrgSettingNumber("routine_cap_per_day", DEFAULT_ROUTINE_CAP);
  const save = useSaveOrgSetting();
  const [draft, setDraft] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const stored = String(cap.data ?? DEFAULT_ROUTINE_CAP);
  const current = draft ?? stored;
  const parsed = parseInt(current, 10);
  const valid = Number.isFinite(parsed) && parsed >= 1 && parsed <= 8;
  const dirty = draft !== null && draft !== stored;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-hairline bg-white p-5 shadow-e1">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Sparkles className="h-4 w-4 text-brand-600" />
          Routine Calls
        </h3>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
            Routine calls per tech-day
            <InfoTip label="How the routine cap works">
              Suggest-a-Time won't put a tune-up on a tech-day that already has this many
              maintenance-looking calls. That keeps same-day room for no-heats and no-cools;
              raise it in shoulder season and lower it in January.
            </InfoTip>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={8}
              value={current}
              disabled={!canManage}
              onChange={(e) => setDraft(e.target.value)}
              className="h-8 w-16 rounded-md border border-hairline bg-white px-2 text-center text-sm font-semibold text-slate-800 disabled:bg-slate-50 disabled:text-slate-400"
              aria-label="Routine calls per tech-day"
            />
            {canManage && dirty && (
              <button
                type="button"
                disabled={!valid || save.isPending}
                onClick={() =>
                  save.mutate(
                    { key: "routine_cap_per_day", value: parsed },
                    {
                      onSuccess: () => {
                        setDraft(null);
                        setSavedFlash(true);
                        window.setTimeout(() => setSavedFlash(false), 2500);
                      },
                    },
                  )
                }
                className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
              >
                {save.isPending ? "Saving…" : "Save"}
              </button>
            )}
            {savedFlash && (
              <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                saved ✓
              </span>
            )}
          </div>
        </div>

        {!canManage && (
          <p className="mt-3 border-t border-slate-100 pt-3 text-[11px] text-slate-400">
            Read-only — changing this needs the "Manage app settings"
            capability.
          </p>
        )}

        {/* BOARD-PCT-01: the denominator behind the board's "% booked". */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-4">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
            Full calls per tech-day
            <InfoTip label="What this number drives">
              The board&apos;s &ldquo;% booked&rdquo; stat divides the day&apos;s stops by
              (working techs × this number). Routine cap above limits tune-up STACKING;
              this one says what a full day looks like overall — most shops run 5–7.
            </InfoTip>
          </div>
          <CapacityKnob canManage={canManage} />
        </div>
      </div>

    </div>
  );
}

/** BOARD-PCT-01 — the "full day" number: board % booked = stops ÷ (techs × this). */
function CapacityKnob({ canManage }: { canManage: boolean }) {
  const cap = useOrgSettingNumber("board_capacity_calls_per_tech", 6);
  const save = useSaveOrgSetting();
  const [draft, setDraft] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const stored = String(cap.data ?? 6);
  const current = draft ?? stored;
  const parsed = parseInt(current, 10);
  const valid = Number.isFinite(parsed) && parsed >= 2 && parsed <= 12;
  const dirty = draft !== null && draft !== stored;
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={2}
        max={12}
        value={current}
        disabled={!canManage}
        onChange={(e) => setDraft(e.target.value)}
        className="h-8 w-16 rounded-md border border-hairline bg-white px-2 text-center text-sm font-semibold text-slate-800 disabled:bg-slate-50 disabled:text-slate-400"
        aria-label="Full calls per tech-day"
      />
      {canManage && dirty && (
        <button
          type="button"
          disabled={!valid || save.isPending}
          onClick={() =>
            save.mutate(
              { key: "board_capacity_calls_per_tech", value: parsed },
              {
                onSuccess: () => {
                  setDraft(null);
                  setSavedFlash(true);
                  window.setTimeout(() => setSavedFlash(false), 2500);
                },
              },
            )
          }
          className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
      )}
      {savedFlash && (
        <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
          saved ✓
        </span>
      )}
    </div>
  );
}
